import { useEffect, useEffectEvent, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, Lock, Play } from "lucide-react";
import * as api from "../lib/api.ts";
import { newClientId } from "../lib/id.ts";
import { chime } from "../lib/chime.ts";
import { notify, notifyError } from "../lib/notify.ts";
import type { Entry } from "../lib/session.ts";
import { describeTarget, isStepDone, nextStep, restAfterStep, roundsOf, stepKey, type Step } from "../lib/circuit.ts";
import type { useTimer } from "../hooks/useTimer.ts";
import { REST_PILL_ROOM, RestTimer } from "./RestTimer.tsx";
import { Stepper } from "./Stepper.tsx";

/**
 * The abs circuit, one movement at a time.
 *
 * Sixteen short efforts in a row, with the phone on the floor: the screen
 * shows the one to do now, big, and moves on by itself. A hold counts down
 * once started — started by hand, because getting into a plank takes a
 * moment — and the round's rest starts after its last movement.
 */
export function CircuitPanel({
  items,
  editable,
  onUnlock,
  timer,
  number,
  total,
  footer,
  onBack,
  onSaved,
}: {
  items: Entry[];
  editable: boolean;
  onUnlock: () => void;
  timer: ReturnType<typeof useTimer>;
  /** The circuit's number in the session list. */
  number: number;
  total: number;
  footer: ReactNode;
  onBack: () => void;
  onSaved: () => void;
}) {
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const increment = settings.data?.plateIncrementKg ?? 2.5;

  // Steps dealt with on this screen: skipped, or saved and not back yet.
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  const [ticking, setTicking] = useState(false);

  const rounds = roundsOf(items);
  const passed = new Set([...skipped, ...saved]);
  const step = nextStep(items, passed);
  const allTicked = items.every((item) => item.doneAt !== null);

  const save = async (at: Step, values: { reps: number; weightKg: number; durationSeconds: number | null }) => {
    const item = items[at.item]!;
    // Taken up before the rest ran out: the rest is over, it is not owed.
    timer.stop();
    setSaved((current) => new Set(current).add(stepKey(at)));
    try {
      const result = await api.saveSet({
        clientId: newClientId(),
        sessionExerciseId: item.id,
        setIndex: at.round,
        reps: values.reps,
        weightKg: values.weightKg,
        dropWeightKg: null,
        rpe: null,
        isWarmup: false,
        notes: null,
        durationSeconds: values.durationSeconds,
        repsRight: null,
        dropReps: null,
        dropRepsRight: null,
        weightKgRight: null,
        dropWeightKgRight: null,
      });
      navigator.vibrate?.(result.queued ? [20, 40, 20] : 25);
      const rest = restAfterStep(items, at);
      if (rest > 0) timer.start(rest);
      if (result.queued) notify("Hors ligne : série gardée, envoyée au retour du réseau.");
      onSaved();
    } catch (error) {
      // Not saved: offer the step again rather than pretend it went through.
      setSaved((current) => {
        const next = new Set(current);
        next.delete(stepKey(at));
        return next;
      });
      notifyError("Série non enregistrée", error);
    }
  };

  const tickAll = async () => {
    setTicking(true);
    try {
      await Promise.all(
        items
          .filter((item) => (item.doneAt !== null) === allTicked)
          .map((item) => api.setExerciseDone(item.id, !allTicked)),
      );
      navigator.vibrate?.(25);
      onSaved();
    } catch (error) {
      notifyError("Pas enregistré", error);
    } finally {
      setTicking(false);
    }
  };

  return (
    <div className="gym mx-auto flex min-h-dvh max-w-2xl flex-col px-4 md:px-8 lg:grid lg:max-w-6xl lg:grid-cols-[minmax(0,1fr)_26rem] lg:content-start lg:gap-14 lg:pt-10">
      <div>
        <header className="safe-top">
          {/* Its place in the session in the corner, as on a movement's screen:
              the big ordinal took two lines of the phone for a number. */}
          <div
            className={[
              "mt-2.5 flex items-center justify-between gap-3",
              timer.startedAt !== null ? REST_PILL_ROOM : "",
            ].join(" ")}
          >
            <button
              type="button"
              onClick={onBack}
              className="hit -ml-1 flex shrink-0 items-center gap-1 py-1 pr-2 text-[0.85rem] text-ink-mute hover:text-ink active:text-ink"
            >
              <ChevronLeft size={16} className="shrink-0" aria-hidden /> Séance
            </button>
            <span className={["label num shrink-0", timer.startedAt !== null ? "max-lg:hidden" : ""].join(" ")}>
              {number + 1} / {total}
            </span>
          </div>

          <h1 className="wide mt-2 text-[1.75rem] leading-tight font-bold md:text-[2.2rem]">
            {items[0]?.circuitGroup}
          </h1>
          <p className="num mt-1 text-[0.9rem] text-ink-soft">
            {rounds} tours · {items.length} mouvements · repos entre les tours
          </p>
        </header>

        <RoundsGrid items={items} rounds={rounds} current={step} skipped={skipped} saved={saved} />
      </div>

      <div className="flex-1 lg:hidden" />

      <div className="sticky bottom-0 z-30 -mx-4 mt-6 space-y-2 border-t border-line/70 bg-base/90 px-4 pt-3 safe-bottom backdrop-blur-lg md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:backdrop-blur-none lg:sticky lg:top-10 lg:bottom-auto lg:mt-0 lg:self-start">
        {timer.startedAt !== null && (
          <RestTimer
            seconds={timer.seconds}
            startedAt={timer.startedAt}
            onDone={noop}
            onDismiss={timer.stop}
            onExpire={timer.stop}
            onAdjust={timer.adjust}
            label="Repos entre les tours"
          />
        )}

        {!editable ? (
          <button
            type="button"
            onClick={onUnlock}
            className="card flex h-14 w-full items-center justify-center gap-2 text-[0.95rem] text-ink-soft active:bg-surface-high"
          >
            <Lock size={16} aria-hidden /> Séance terminée · Corriger
          </button>
        ) : step ? (
          <StepCard
            key={stepKey(step)}
            items={items}
            step={step}
            rounds={rounds}
            increment={increment}
            onStart={timer.stop}
            onDone={(values) => void save(step, values)}
            onSkip={() => setSkipped((current) => new Set(current).add(stepKey(step)))}
          />
        ) : (
          <div className="card px-5 py-4">
            <p className="label flex items-center gap-1.5 text-ink-soft">
              <Check size={14} strokeWidth={3} aria-hidden /> Circuit terminé
            </p>
            {skipped.size > 0 && (
              <button
                type="button"
                onClick={() => setSkipped(new Set())}
                className="mt-1 text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink"
              >
                Reprendre les {skipped.size} passé{skipped.size > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}

        {editable && (
          <button
            type="button"
            onClick={() => void tickAll()}
            disabled={ticking}
            className="h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-60"
          >
            {allTicked ? "Décocher le circuit" : "Tout fait, sans le détail"}
          </button>
        )}

        <div className="flex gap-2 pt-1">{footer}</div>
      </div>
    </div>
  );
}

/** One row per round, one mark per movement: where the circuit stands. */
function RoundsGrid({
  items,
  rounds,
  current,
  skipped,
  saved,
}: {
  items: Entry[];
  rounds: number;
  current: Step | null;
  skipped: ReadonlySet<string>;
  saved: ReadonlySet<string>;
}) {
  return (
    <div className="mt-6">
      <ol className="space-y-1.5">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-baseline gap-2.5 text-[0.9rem]">
            <span className="num w-4 shrink-0 text-ink-mute">{index + 1}</span>
            <span className="min-w-0 flex-1 truncate">{item.exercise.name}</span>
            <span className="num shrink-0 text-[0.82rem] text-ink-mute">{describeTarget(item)}</span>
          </li>
        ))}
      </ol>

      <div className="mt-5 space-y-1.5" aria-label="Avancement du circuit">
        {Array.from({ length: rounds }, (_, r) => {
          const round = r + 1;
          return (
            <div key={round} className="flex items-center gap-2.5">
              <span className="num w-7 shrink-0 text-[0.75rem] text-ink-mute">T{round}</span>
              <div className="flex flex-1 gap-1.5">
                {items.map((item, index) => {
                  const key = stepKey({ round, item: index });
                  const done = isStepDone(item, round) || saved.has(key);
                  const isCurrent = current?.round === round && current.item === index;
                  return (
                    <span
                      key={item.id}
                      title={`${item.exercise.name}, tour ${round}`}
                      className={[
                        "num flex h-7 flex-1 items-center justify-center rounded-md text-[0.72rem] font-bold transition-colors",
                        done
                          ? "bg-ink text-[var(--color-base)]"
                          : isCurrent
                            ? "bg-surface-high text-ink ring-2 ring-ink/70"
                            : skipped.has(key)
                              ? "bg-iron/30 text-ink-mute"
                              : "bg-surface-high text-ink-mute",
                      ].join(" ")}
                    >
                      {index + 1}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The movement to do now: reps to confirm, or a hold to time, and its load if it takes one. */
function StepCard({
  items,
  step,
  rounds,
  increment,
  onStart,
  onDone,
  onSkip,
}: {
  items: Entry[];
  step: Step;
  rounds: number;
  increment: number;
  /** A hold started: whatever rest was running ends there. */
  onStart: () => void;
  onDone: (values: { reps: number; weightKg: number; durationSeconds: number | null }) => void;
  onSkip: () => void;
}) {
  const item = items[step.item]!;
  const following = items[step.item + 1] ?? null;
  const rest = restAfterStep(items, step);
  const hold = item.targetSeconds;

  const [reps, setReps] = useState(firstNumber(item.targetReps) ?? 10);
  // This card replaces the previous step's under the same thumb: the second
  // half of a double tap would validate a movement not even started.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const arm = setTimeout(() => setArmed(true), 400);
    return () => clearTimeout(arm);
  }, []);
  const [weight, setWeight] = useState(lastLoad(item));

  // A hold, side by side: started by hand once in position, then counted down.
  const sides = item.perSide ? 2 : 1;
  const [side, setSide] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const remaining = hold === null || startedAt === null ? hold : Math.max(0, hold - (now - startedAt) / 1000);

  const values = (durationSeconds: number | null) => ({
    reps: hold === null ? reps : 0,
    weightKg: item.loadHint ? weight : 0,
    durationSeconds,
  });

  // Time's up: the other side next, or the step is done. An effect event,
  // so the countdown below does not restart whenever the reps change.
  const timeUp = useEffectEvent(() => {
    chime();
    setStartedAt(null);
    if (side + 1 < sides) setSide(side + 1);
    else onDone(values(hold));
  });

  useEffect(() => {
    if (startedAt === null || hold === null) return;
    const tick = setInterval(() => {
      const at = Date.now();
      setNow(at);
      if (at - startedAt >= hold * 1000) {
        clearInterval(tick);
        timeUp();
      }
    }, 200);
    return () => clearInterval(tick);
  }, [startedAt, hold]);

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="label">
          Tour {step.round} / {rounds}
        </p>
        <p className="num text-[0.8rem] text-ink-mute">
          {step.item + 1} / {items.length}
        </p>
      </div>
      <h2 className="wide mt-1.5 text-[1.45rem] leading-tight font-bold">{item.exercise.name}</h2>

      {hold !== null ? (
        <div className="mt-3 flex items-end justify-between gap-3">
          <span className="display text-[3.4rem] leading-none font-bold">{formatClock(remaining ?? hold)}</span>
          {sides > 1 && (
            <span className="mb-1.5 text-[0.9rem] text-ink-soft">{side === 0 ? "1er côté" : "2e côté"}</span>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <Stepper label="répétitions" value={reps} step={1} decimals={0} max={100} onChange={setReps} />
        </div>
      )}

      {item.loadHint && (
        <div className="mt-2">
          <Stepper
            label={`charge · ${item.loadHint}`}
            unit="kg"
            value={weight}
            step={increment}
            decimals={1}
            onChange={setWeight}
            caption={<span className="text-ink-mute">facultatif, 0 si sans charge</span>}
          />
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onSkip}
          disabled={!armed}
          className="card h-14 w-24 shrink-0 text-[0.9rem] text-ink-soft active:bg-surface-high"
        >
          Passer
        </button>
        {hold !== null && startedAt === null ? (
          <button
            type="button"
            onClick={() => {
              onStart();
              setNow(Date.now());
              setStartedAt(Date.now());
            }}
            disabled={!armed}
            className="wide slab flex h-14 flex-1 items-center justify-center gap-2 rounded-card text-[1.1rem] font-bold"
          >
            <Play size={18} strokeWidth={2.5} aria-hidden /> {side === 0 ? "Lancer" : "Lancer le 2e côté"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onDone(values(hold === null ? null : Math.round(hold - (remaining ?? 0))))}
            disabled={!armed}
            className="wide slab flex h-14 flex-1 items-center justify-center gap-2 rounded-card text-[1.1rem] font-bold"
          >
            <Check size={20} strokeWidth={3} aria-hidden /> Fait
          </button>
        )}
      </div>

      {hold !== null && startedAt === null && side === 0 && (
        <button
          type="button"
          onClick={() => onDone(values(hold))}
          disabled={!armed}
          className="mt-1 h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink"
        >
          Fait sans chrono
        </button>
      )}

      <p className="mt-3 truncate text-[0.82rem] text-ink-mute">
        {following
          ? `Ensuite : ${following.exercise.name} · ${describeTarget(following)}`
          : rest > 0
            ? `Ensuite : repos ${formatClock(rest)}, puis tour ${step.round + 1}`
            : step.round < rounds
              ? `Ensuite : tour ${step.round + 1}`
              : "Dernier mouvement du circuit"}
      </p>
    </div>
  );
}

/** The timer announces itself; nothing else has to happen when it ends. */
function noop() {}

function firstNumber(text: string | null): number | null {
  const match = text?.match(/\d+/);
  return match ? Number.parseInt(match[0], 10) : null;
}

/** The load used last: this session's earlier round, else last time. */
function lastLoad(item: Entry): number {
  const loaded = (sets: { weightKg: number }[] | undefined) => sets?.filter((set) => set.weightKg > 0).at(-1)?.weightKg;
  return loaded(item.sets) ?? loaded(item.previous?.sets) ?? 0;
}

/** 75 -> "1:15". */
function formatClock(seconds: number): string {
  const whole = Math.ceil(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
